import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ClaimModal({ open, onOpenChange, message, onHeard }) {
  const [step, setStep] = useState("form"); // form | verify | done
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [evidence, setEvidence] = useState("");
  const [publicBadge, setPublicBadge] = useState(false);
  const [claimId, setClaimId] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const submitClaim = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/messages/${message.id}/claim`,
        { email, display_name: name, evidence, method: "email_link",
          verifier_visibility: publicBadge && name ? "public" : "private" });
      setClaimId(r.data.claim_id);
      setToken(r.data.token); // in this build the token is returned; production sends by email.
      setStep("verify");
      toast.success("Claim opened. Verify to continue.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open claim.");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/claims/verify", { token });
      const r2 = await api.post(`/claims/${claimId}/heard`, {});
      setStep("done");
      toast.success("Message marked as HEARD.");
      onHeard?.(r2.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[color:var(--oh-bg)] border oh-hairline rounded-none max-w-md p-0"
                     data-testid="claim-modal">
        <div className="px-8 sm:px-10 py-10">
          <DialogTitle className="oh-label">Is this message for you?</DialogTitle>
          <h2 className="font-serif-editorial text-3xl sm:text-4xl leading-[1.05] mt-3">
            Claim this message.
          </h2>
          {step === "form" && (
            <form onSubmit={submitClaim} className="mt-6 space-y-5">
              <p className="text-[color:var(--oh-ink-2)] text-sm leading-relaxed">
                Only a verified recipient — or an authorised representative — may mark
                a MESSAGE as HEARD. We never publish your evidence.
              </p>
              <div>
                <label className="oh-label">Your official email</label>
                <input type="email" required value={email}
                       onChange={(e) => setEmail(e.target.value)}
                       placeholder="you@your-organisation.org"
                       className="oh-input" data-testid="claim-email" />
              </div>
              <div>
                <label className="oh-label">Name or organisation</label>
                <input type="text" value={name}
                       onChange={(e) => setName(e.target.value)}
                       placeholder="How you should appear"
                       className="oh-input" data-testid="claim-name" />
              </div>
              <div>
                <label className="oh-label">Evidence (optional)</label>
                <textarea value={evidence} rows={3}
                          onChange={(e) => setEvidence(e.target.value)}
                          placeholder="Link, role, or short statement"
                          className="oh-input resize-none" data-testid="claim-evidence" />
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={publicBadge}
                          onCheckedChange={(v) => setPublicBadge(!!v)}
                          disabled={!name}
                          className="mt-1"
                          data-testid="claim-public-badge" />
                <span className="text-sm text-[color:var(--oh-ink-2)] leading-relaxed">
                  Show my name publicly on the HEARD ✓ badge
                  <span className="block oh-label mt-1 text-[color:var(--oh-ink-3)]">
                    Optional. Only the display name above is shown — never your email or evidence.
                  </span>
                </span>
              </label>
              <button type="submit" disabled={busy} className="btn-primary w-full"
                      data-testid="claim-submit">
                {busy ? "Opening…" : "Open claim"}
              </button>
            </form>
          )}
          {step === "verify" && (
            <div className="mt-6 space-y-5">
              <p className="text-[color:var(--oh-ink-2)] text-sm leading-relaxed">
                We sent a secure single-use link to {email}. Paste the token below to
                verify — or use the link in your inbox.
              </p>
              <div>
                <label className="oh-label">Verification token</label>
                <input value={token} onChange={(e) => setToken(e.target.value)}
                       className="oh-input" data-testid="claim-token" />
              </div>
              <button onClick={verify} disabled={busy || !token}
                      className="btn-red w-full" data-testid="claim-heard">
                {busy ? "Verifying…" : "I heard you"}
              </button>
              <p className="text-xs text-[color:var(--oh-ink-3)]">
                Pressing I HEARD YOU makes the MESSAGE publicly HEARD ✓ and quietly
                tells every human who added a VOICE.
              </p>
            </div>
          )}
          {step === "done" && (
            <div className="mt-6">
              <div className="oh-label text-[color:var(--oh-red)]">Heard ✓</div>
              <p className="font-serif-editorial text-2xl mt-3 leading-tight">
                Thank you — this MESSAGE has been received.
              </p>
              <button onClick={() => onOpenChange(false)}
                      className="btn-primary w-full mt-6" data-testid="claim-close">
                Done
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
